// Chess Tools API Service
// Handles all API calls to api.chesstools.org for FIDE player data

const API_BASE_URL = 'https://api.chesstools.org';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

// Simple in-memory cache
const cache = new Map();

// Helper function to handle API calls with caching
const fetchWithCache = async (endpoint, cacheKey) => {
  // Check cache first
  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      mode: 'cors'
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    // Cache the successful response
    cache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });

    return data;
  } catch (error) {
    // If we have cached data, return it even if expired during error
    if (cached) {
      console.warn('Using stale cache due to API error:', error);
      return cached.data;
    }
    throw error;
  }
};

// Get top active FIDE players
export const getTopActivePlayers = async (limit = 100) => {
  try {
    const data = await fetchWithCache('/fide/top_active/', 'top_active');
    // Return limited results if specified
    return limit ? data.slice(0, limit) : data;
  } catch (error) {
    console.error('Error fetching top active players:', error);
    throw error;
  }
};

// Get top FIDE players by rating
export const getTopPlayersByRating = async (limit = 100) => {
  try {
    const data = await fetchWithCache('/fide/top_by_rating', 'top_by_rating');
    // Return limited results if specified
    return limit ? data.slice(0, limit) : data;
  } catch (error) {
    console.error('Error fetching top players by rating:', error);
    throw error;
  }
};

// Get player history by FIDE ID
export const getPlayerHistory = async (playerId) => {
  if (!playerId) {
    throw new Error('Player ID is required');
  }
  
  try {
    const data = await fetchWithCache(`/fide/player_history/${playerId}`, `player_history_${playerId}`);
    return data;
  } catch (error) {
    console.error(`Error fetching player history for ${playerId}:`, error);
    throw error;
  }
};

// Clear cache manually if needed
export const clearCache = () => {
  cache.clear();
};

// Get cache status
export const getCacheStatus = () => {
  const status = {};
  cache.forEach((value, key) => {
    const age = Date.now() - value.timestamp;
    status[key] = {
      age: Math.floor(age / 1000) + 's',
      expired: age > CACHE_DURATION
    };
  });
  return status;
};

// Format player data for display
export const formatPlayerData = (player) => {
  return {
    id: player.id || player.fide_id || '',
    name: player.name || `${player.first_name || ''} ${player.last_name || ''}`.trim() || 'Unknown Player',
    rating: player.rating || player.standard_rating || 0,
    country: player.country || player.federation || 'Unknown',
    title: player.title || 'GM',
    age: player.age || (player.birth_year ? new Date().getFullYear() - player.birth_year : 0) || 0,
    games: player.games || player.games_played || 0,
    ratingChange: player.rating_change || 0,
    rank: player.rank || player.world_rank || 0
  };
};

// Proxy function if CORS issues arise (to be implemented server-side if needed)
export const getProxyUrl = (endpoint) => {
  // If we need to use a proxy (e.g., Firebase Functions), update this
  return `${API_BASE_URL}${endpoint}`;
};